// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, User, LogOut, LayoutDashboard, CreditCard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services/auth';
import { getDashboardOrigin } from '@/utils/hostOrigins';
import ArrangellyBrand from '@/components/brand/ArrangellyBrand';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const profileDropdownRef = React.useRef<HTMLDivElement>(null);

  const navLinks: { name: string; path: string }[] = [
    { name: 'Features', path: '/features' },
    { name: 'Partnerships', path: '/partnerships' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Promo', path: '/promo' },
    { name: 'Contact', path: '/contact' },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
    };
    if (isProfileDropdownOpen) {
      // Use a small delay to prevent immediate closure when opening
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isProfileDropdownOpen]);

  return (
    <header className="w-full pt-4 pb-2 z-50">
      <nav className="container mx-auto px-4">
        <div className="relative z-10 mx-auto max-w-6xl bg-white/80 backdrop-blur-md border border-slate-200/90 shadow-sm shadow-slate-200/50 rounded-full px-6 py-3 flex items-center justify-between overflow-visible">
          <Link
            to="/"
            className="group select-none shrink-0 min-w-0 hover:opacity-95 transition-opacity"
            aria-label="Arrangelly home"
          >
            <ArrangellyBrand size="lg" layout="horizontal" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-sm font-medium transition-colors relative group ${
                  location.pathname === link.path
                    ? 'text-violet-800'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-accent transition-all ${
                    location.pathname === link.path
                      ? 'w-full'
                      : 'w-0 group-hover:w-full'
                  }`}
                ></span>
              </Link>
            ))}
          </div>

          {/* Right Side: User Profile or Login */}
          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated && user ? (
              <div className="relative" ref={profileDropdownRef}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsProfileDropdownOpen(!isProfileDropdownOpen);
                  }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100/80 transition-colors"
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-secondary border border-violet-200 shadow-glow overflow-hidden flex items-center justify-center text-primary-foreground text-sm font-semibold">
                    {user.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        alt="Profile avatar"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      user.firstName?.[0] || user.email[0].toUpperCase()
                    )}
                  </div>
                  <ChevronDown size={16} className="text-slate-500" />
                </button>
                
                {/* Profile Dropdown */}
                <AnimatePresence>
                  {isProfileDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-lg shadow-lg shadow-slate-200/80 overflow-hidden z-50"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-3 border-b border-slate-100">
                        <p className="text-sm font-medium text-slate-900">
                          {user.firstName && user.lastName 
                            ? `${user.firstName} ${user.lastName}`
                            : user.email}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">{user.email}</p>
                        {user.role && (
                          <p className="text-xs text-violet-600 mt-1 capitalize">{user.role}</p>
                        )}
                      </div>
                      <div className="py-1">
                        <Link
                          to="/billing"
                          onClick={() => setIsProfileDropdownOpen(false)}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                        >
                          <CreditCard size={16} />
                          Billing
                        </Link>
                        <button
                          onClick={() => {
                            setIsProfileDropdownOpen(false);
                            window.location.href = `${getDashboardOrigin()}/`;
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                        >
                          <LayoutDashboard size={16} />
                          Go to Dashboard
                        </button>
                        <button
                          onClick={() => {
                            logout();
                            setIsProfileDropdownOpen(false);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                        >
                          <LogOut size={16} />
                          Logout
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <>
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-secondary border border-violet-200 shadow-glow overflow-hidden" />
                <Button
                  variant="ghost"
                  className="text-slate-700 hover:text-slate-900 hover:bg-slate-100/80 text-sm px-4"
                  onClick={() => {
                    window.location.href = `${getDashboardOrigin()}/login`;
                  }}
                >
                  Login
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-slate-800 p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden overflow-hidden bg-white/95 border-t border-slate-200 mt-4 rounded-2xl shadow-sm"
            >
              <div className="flex flex-col gap-4 py-6">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`text-base font-medium px-4 ${
                      location.pathname === link.path
                        ? 'text-violet-800'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="h-px bg-slate-200 mx-4 my-2"></div>
                <div className="flex flex-col gap-3 px-4">
                  {isAuthenticated && user ? (
                    <>
                      <div className="px-2 py-2">
                        <p className="text-sm font-medium text-slate-900">
                          {user.firstName && user.lastName 
                            ? `${user.firstName} ${user.lastName}`
                            : user.email}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">{user.email}</p>
                      </div>
                      <Link
                        to="/billing"
                        onClick={() => setIsMenuOpen(false)}
                        className="w-full flex items-center gap-2 px-2 py-2 text-sm text-slate-600 hover:text-slate-900 justify-start"
                      >
                        <CreditCard size={16} />
                        Billing
                      </Link>
                      <button
                        onClick={() => {
                          setIsMenuOpen(false);
                          window.location.href = `${getDashboardOrigin()}/`;
                        }}
                        className="w-full flex items-center gap-2 px-2 py-2 text-sm text-slate-600 hover:text-slate-900 justify-start"
                      >
                        <LayoutDashboard size={16} />
                        Go to Dashboard
                      </button>
                      <button
                        onClick={() => {
                          logout();
                          setIsMenuOpen(false);
                        }}
                        className="flex items-center gap-2 px-2 py-2 text-sm text-slate-600 hover:text-slate-900 justify-start"
                      >
                        <LogOut size={16} />
                        Logout
                      </button>
                    </>
                  ) : (
                    <Button
                      variant="ghost"
                      className="justify-start text-slate-600"
                      onClick={() => {
                        setIsMenuOpen(false);
                        window.location.href = `${getDashboardOrigin()}/login`;
                      }}
                    >
                      Login
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;


